import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProfileCard from './ProfileCard';
import { ArrowLeft, Mail, Phone, Building, MapPin, Award, Shield, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

const PublicPassportView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/users/${userId}/public-profile/`);
        setUserData(response.data);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user profile. This profile may be private or does not exist.');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  // Generate QR code URL
  const qrCodeUrl = userData
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(window.location.href)}`
    : '';

  // Get user skills/qualifications
  const getUserSkills = () => {
    if (!userData?.qualifications || userData.qualifications.length === 0) {
      return ['Platform Member', 'Certified Professional'];
    }

    return userData.qualifications
      .slice(0, 6)
      .map(q => q.qualification_type_name || q.name);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-white text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Profile Not Found</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-4 px-4 overflow-y-auto">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Login</span>
          </button>
          <img
            src="https://ventro.energy/ventro.png"
            alt="Ventro Logo"
            className="h-6"
          />
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Left Column - Profile Card */}
          <div className="flex flex-col items-center">
            <div className="mb-4 text-center">
              <h1 className="text-2xl font-bold text-white mb-1">
                Powerpass
              </h1>
              <p className="text-sm text-gray-400">
                Digital Identity Card
              </p>
            </div>

            <div className="transform scale-90 hover:scale-95 transition-transform duration-300">
              <ProfileCard
                name={`${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'User'}
                title={userData.position || userData.company_name || 'Platform Member'}
                handle={`${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'user'}
                status={`WINDA ID: ${userData.winda_id || 'N/A'}`}
                contactText=""
                avatarUrl={userData.profile_picture || 'https://via.placeholder.com/400'}
                showUserInfo={true}
                enableTilt={true}
                enableMobileTilt={true}
                qrCodeUrl={qrCodeUrl}
                iconUrl=""
                grainUrl=""
                showBehindGradient={true}
                skills={getUserSkills()}
              />
            </div>

            {/* Verification Badge */}
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium text-green-400">Verified Member</span>
            </div>
          </div>

          {/* Right Column - Detailed Information */}
          <div className="space-y-4">
            {/* Contact Information */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-400" />
                Contact Information
              </h2>
              <div className="space-y-2">
                {userData.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-400">Email</p>
                      <p className="text-white">{userData.email}</p>
                    </div>
                  </div>
                )}
                {userData.phone_number && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-400">Phone</p>
                      <p className="text-white">{userData.phone_number}</p>
                    </div>
                  </div>
                )}
                {userData.personal_address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-400">Address</p>
                      <p className="text-white">{userData.personal_address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Professional Information */}
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
              <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Building className="w-5 h-5 text-purple-400" />
                Professional Information
              </h2>
              <div className="space-y-2">
                {userData.company_name && (
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-400">Company</p>
                      <p className="text-white">{userData.company_name}</p>
                    </div>
                  </div>
                )}
                {userData.position && (
                  <div className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-400">Position</p>
                      <p className="text-white">{userData.position}</p>
                    </div>
                  </div>
                )}
                {userData.department && (
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-400">Department</p>
                      <p className="text-white">{userData.department}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Qualifications */}
            {userData.qualifications && userData.qualifications.length > 0 && (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20">
                <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-400" />
                  Qualifications & Certifications
                </h2>
                <div className="space-y-2">
                  {userData.qualifications.slice(0, 10).map((qual, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10"
                    >
                      <span className="text-white text-sm">
                        {qual.qualification_type_name || qual.name}
                      </span>
                      {qual.expiry_date && (
                        <span className="text-xs text-gray-400">
                          Exp: {new Date(qual.expiry_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-lg rounded-xl p-4 border border-white/20 text-center">
              <h3 className="text-lg font-bold text-white mb-2">
                Join Ventro Platform
              </h3>
              <p className="text-gray-300 text-sm mb-4">
                Connect with professionals and manage your wind energy operations
              </p>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Get Started
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-gray-400 text-xs">
          <p>
            ventro.energy © {new Date().getFullYear()} | Digital Identity Platform
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicPassportView;
